
import * as React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  defaultMonth,
  ...props
}: CalendarProps) {
  // Default to current month if not provided
  const currentMonth = defaultMonth || new Date();
  
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      defaultMonth={currentMonth}
      className={cn("p-0 w-full flex justify-center pt-1 relative items-center m-auto pointer-events-auto", className)}
      classNames={{
        months: "flex flex-col w-full",
        month: "space-y-4 w-full",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-lg font-semibold",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline", size: "lg" }), 
          "size-9 border-zinc-200 hover:border-zinc-300 p-0 hover:opacity-100 rounded-full"
        ),
        nav_button_previous: "absolute left-3",
        nav_button_next: "absolute right-3",
        table: "w-full border-collapse space-y-1",
        head_row: "flex w-full",
        head_cell: cn(
          "text-black-700 rounded-md font-semibold text-md flex-1 text-center p-0.5",
          "focus-within:relative focus-within:z-20"
        ),

        row: "flex w-full mt-1",
        cell: cn(
          "flex-1 p-0.5 text-center",
          "focus-within:relative focus-within:z-20"
        ),
        day: cn(
          "size-10 md:size-6 lg:size-9 items-center justify-center rounded-full text-sm font-bold bg-sky-100 border-none shadow-none border-zinc-100",
          "hover:bg-blue-200 hover:text-accent-foreground",
          "aria-selected:bg-blue-600 aria-selected:border-blue-600 aria-selected:text-white",
          "focus:bg-blue-600 focus:text-white"
        ),
        day_range_end: "day-range-end",
        day_selected: "bg-blue-400 !text-white !hover:bg-blue-700 !focus:bg-blue-700",
        day_today: "bg-green-100 !text-blue-700",
        day_outside: "",
        day_disabled: "text-gray-400 opacity-50 !cursor-not-allowed !hover:bg-transparent !border-none !shadow-none !bg-gray-50 !pointer-events-none cursor-not-allowed",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => <ArrowLeft className="h-6 w-6 md:w-4 md:h-4" />,
        IconRight: ({ ..._props }) => <ArrowRight className="h-6 w-6 md:w-4 md:h-4" />
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
